import { useState } from 'react';
import { GENRES, REGIONS, TV_STATUS, YEARS, SORT_OPTIONS, type MediaFilters } from '@/lib/media';

interface FilterPanelProps {
  type: 'movie' | 'tv';
  filters: MediaFilters;
  onChange: (filters: MediaFilters) => void;
}

interface RowProps {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onSelect: (value: string) => void;
}

function FilterRow({ label, options, value, onSelect }: RowProps) {
  return (
    <div className="flex flex-col gap-2 py-3 md:flex-row md:items-start md:gap-4">
      <span className="shrink-0 pt-1 text-sm font-medium text-foreground-500 md:w-16">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onSelect(opt.value)}
            className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
              value === opt.value
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterPanel({ type, filters, onChange }: FilterPanelProps) {
  const [expanded, setExpanded] = useState(false);

  const update = (patch: Partial<MediaFilters>) => {
    onChange({ ...filters, ...patch });
  };

  const reset = () => {
    onChange({ genre: 'all', region: 'all', year: 'all', status: 'all', sort: 'hot' });
  };

  const toOptions = (list: string[]) => [
    { value: 'all', label: '全部' },
    ...list.map((v) => ({ value: v, label: v })),
  ];

  return (
    <div className="rounded-lg bg-background-50 p-4 ring-1 ring-background-200/70 md:p-5">
      <div className="flex items-center justify-between md:hidden">
        <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
          <i className="ri-filter-3-line text-base text-accent-600" />
          筛选条件
        </span>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1 whitespace-nowrap text-sm text-foreground-600"
        >
          {expanded ? '收起' : '展开'}
          <i className={expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
        </button>
      </div>

      <div className={`${expanded ? 'block' : 'hidden'} md:block`}>
        <div className="divide-y divide-background-200/70">
          <FilterRow
            label="类型"
            options={toOptions(GENRES)}
            value={filters.genre || 'all'}
            onSelect={(v) => update({ genre: v })}
          />
          <FilterRow
            label="地区"
            options={toOptions(REGIONS)}
            value={filters.region || 'all'}
            onSelect={(v) => update({ region: v })}
          />
          <FilterRow
            label="年份"
            options={toOptions(YEARS.map((y) => String(y)))}
            value={filters.year || 'all'}
            onSelect={(v) => update({ year: v })}
          />
          {type === 'tv' && (
            <FilterRow
              label="状态"
              options={toOptions(TV_STATUS)}
              value={filters.status || 'all'}
              onSelect={(v) => update({ status: v })}
            />
          )}
          <FilterRow
            label="排序"
            options={SORT_OPTIONS}
            value={filters.sort || 'hot'}
            onSelect={(v) => update({ sort: v })}
          />
        </div>

        <div className="flex justify-end pt-3">
          <button
            type="button"
            onClick={reset}
            className="flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-sm text-foreground-500 transition-colors hover:text-accent-600"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}